import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import LOGO_SRC from "@/assets/NischalamIcon.jpeg"

const primaryLinks = [
  { to: "/",         label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/find-us",  label: "Find Us" },
];

const aboutChildren = [
  { to: "/about",              label: "About Us",                        desc: "Foundation overview & our mission" },
  { to: "/foundation/english", label: "Nischalam Foundation in English", desc: "The full story — English" },
  { to: "/foundation/telugu",  label: "Nischalam Foundation in Telugu",  desc: "నిశ్చలం ఫౌండేషన్ — తెలుగులో" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [aboutMobileOpen, setAboutMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const isAboutSection =
    pathname.startsWith("/about") || pathname.startsWith("/foundation");

  useEffect(() => {
    // On the home page, keep the navbar transparent while the user is over the
    // hero section. Only turn solid once the viewport has scrolled past ~90% of
    // the hero height (i.e. when the "Our Categories" section is about to appear).
    // On every other page the navbar is solid immediately.
    const onScroll = () => {
      if (isHome) {
        const threshold = Math.max(320, window.innerHeight * 0.9);
        setScrolled(window.scrollY > threshold);
      } else {
        setScrolled(true);
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [isHome]);

  useEffect(() => { setOpen(false); setAboutMobileOpen(false); setAboutOpen(false); }, [pathname]);

  const solid = scrolled || !isHome || open;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        solid ? "bg-white/85 backdrop-blur-xl border-b border-border" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className={`flex items-center gap-3 font-display text-2xl tracking-tight ${solid ? "text-forest" : "text-white"}`}
        >
          <span className={`w-11 h-11 rounded-full overflow-hidden grid place-items-center bg-white ${solid ? "" : "ring-2 ring-white/40"}`}>
            <img src={LOGO_SRC} alt="Nischalam logo" className="w-full h-full object-cover" />
          </span>
          Nischalam
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {primaryLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-sm tracking-wide link-underline ${
                  solid
                    ? isActive ? "text-forest font-semibold" : "text-neutral-700 hover:text-forest"
                    : isActive ? "text-white font-semibold" : "text-white/85 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}

          <div
            className="relative"
            onMouseEnter={() => setAboutOpen(true)}
            onMouseLeave={() => setAboutOpen(false)}
          >
            <NavLink
              to="/about"
              data-testid="nav-link-about"
              className={({ isActive }) =>
                `text-sm tracking-wide inline-flex items-center gap-1 link-underline ${
                  solid
                    ? (isActive || isAboutSection) ? "text-forest font-semibold" : "text-neutral-700 hover:text-forest"
                    : (isActive || isAboutSection) ? "text-white font-semibold" : "text-white/85 hover:text-white"
                }`
              }
            >
              About Us
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${aboutOpen ? "rotate-180" : ""}`} />
            </NavLink>

            <AnimatePresence>
              {aboutOpen && (
                <motion.div
                  data-testid="about-dropdown"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.18 }}
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-3"
                >
                  <div className="w-[340px] rounded-2xl bg-white border border-border shadow-[0_20px_60px_-20px_rgb(30,63,32,0.35)] p-2">
                    {aboutChildren.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        data-testid={`nav-about-${c.to.replace(/\//g, "-").replace(/^-/,"")}`}
                        className={({ isActive }) =>
                          `block rounded-xl px-4 py-3 transition-colors ${
                            isActive ? "bg-forest/10 text-forest" : "text-neutral-800 hover:bg-beige"
                          }`
                        }
                      >
                        <p className="font-medium text-[15px] leading-tight">{c.label}</p>
                        <p className="text-xs text-neutral-500 mt-1">{c.desc}</p>
                      </NavLink>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <NavLink
            to="/contact"
            data-testid="nav-link-contact"
            className={({ isActive }) =>
              `text-sm tracking-wide link-underline ${
                solid
                  ? isActive ? "text-forest font-semibold" : "text-neutral-700 hover:text-forest"
                  : isActive ? "text-white font-semibold" : "text-white/85 hover:text-white"
              }`
            }
          >
            Contact
          </NavLink>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`md:hidden w-11 h-11 rounded-full grid place-items-center ${
            solid ? "text-forest border border-border bg-white/60" : "text-white border border-white/40"
          }`}
          aria-label="Toggle menu"
        >
          <AnimatePresence mode="wait">
            {open ? (
              <motion.span key="x" initial={{rotate:-90, opacity:0}} animate={{rotate:0, opacity:1}} exit={{rotate:90, opacity:0}}>
                <X className="w-5 h-5" />
              </motion.span>
            ) : (
              <motion.span key="m" initial={{rotate:90, opacity:0}} animate={{rotate:0, opacity:1}} exit={{rotate:-90, opacity:0}}>
                <Menu className="w-5 h-5" />
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden border-t border-border bg-white"
          >
            <div className="px-6 py-6 flex flex-col gap-3">
              {primaryLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `text-lg font-display py-1 ${isActive ? "text-forest" : "text-neutral-800"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}

              <div>
                <button
                  data-testid="mobile-about-toggle"
                  onClick={() => setAboutMobileOpen((v) => !v)}
                  className={`w-full flex items-center justify-between text-lg font-display py-1 ${
                    isAboutSection ? "text-forest" : "text-neutral-800"
                  }`}
                >
                  About Us
                  <ChevronDown className={`w-4 h-4 transition-transform ${aboutMobileOpen ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence>
                  {aboutMobileOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden pl-4 mt-2 border-l border-border space-y-2"
                    >
                      {aboutChildren.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          data-testid={`mobile-nav-about-${c.to.replace(/\//g, "-").replace(/^-/,"")}`}
                          className={({ isActive }) => `block py-2 text-neutral-700 ${isActive ? "text-forest font-medium" : ""}`}
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <NavLink
                to="/contact"
                data-testid="mobile-nav-contact"
                className={({ isActive }) =>
                  `text-lg font-display py-1 ${isActive ? "text-forest" : "text-neutral-800"}`
                }
              >
                Contact
              </NavLink>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
